import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { map, of, switchMap } from 'rxjs';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Asociacion, AsociacionesService, Asociado, ResponseAsociaciones } from '../../../../api';
import { EjercicioVigenteService } from '../../../services/ejercicio-vigente.service';
import { AsociacionAsociadosService } from '../../../services/asociacion-asociados.service';
import { NavegacionService } from '../../../services/navegacion.service';
import { MigasService } from '../../../services/migas.service';
import { PermisosService } from '../../../services/permisos.service';
import { mensajeErrorCarga } from '../../../core/http-errors';
import { ErrorFichaComponent } from '../../error-ficha/error-ficha.component';
import { RestablecerPasswordComponent } from '../../restablecer-password/restablecer-password.component';
import { ItemType, FunctionsAvailable } from '../../formulario/request.model';
import { ItemEvent, FormEvent } from '../../item/item.model';
import { FormularioComponent } from '../../formulario/formulario.component';
import { FormularioService } from '../../../services/formulario.service';
import { Field } from '../../formulario/field.model';
import { FORM_BUTTONS } from '../../formulario/form-buttons.config';
import { FormButtonType } from '../../formulario/formButtonType.enum';
import { UpdatePasswordComponent } from '../../update-password/update-password.component';
import { AlertService } from '../../alert/alert.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ListadoComponent } from '../../listado/listado.component';
import { tieneFoto } from '../../../core/foto.util';
import { environment } from '../../../../environments/environment';
import { LocationPickerComponent, StructuredLocation } from '../../location-picker/location-picker.component';
import { EstadoAsociacionComponent } from '../estado-asociacion/estado-asociacion.component';
import { EstadoAccesoEditorComponent } from '../estado-acceso-editor/estado-acceso-editor.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import { PageHeaderComponent } from '../../ui/page-header.component';
import { TabsNavDirective } from '../../ui/tabs-nav.directive';

export enum ViewModeAsociado {
  INFO = 'info',
  CONTACT = 'contact',
  ASOCIADOS = 'asociados',
  CHANGE_PASSWORD = 'change_password'
}

@Component({
  selector: 'app-asociacion',
  standalone: true,
  imports: [CommonModule, FormularioComponent, UpdatePasswordComponent, ReactiveFormsModule, ListadoComponent, LocationPickerComponent, EstadoAsociacionComponent, EstadoAccesoEditorComponent, ErrorFichaComponent, RestablecerPasswordComponent, RouterLink, SpinnerComponent, PageHeaderComponent, TabsNavDirective],
  templateUrl: './asociacion.component.html',
  styleUrl: './asociacion.component.scss'
})
export class AsociacionComponent {
  ViewModeAsociado = ViewModeAsociado;
  ItemType = ItemType;
  FunctionsAvailable = FunctionsAvailable;
  filesBasePath = environment.FILES_BASE_PATH;
  tieneFoto = tieneFoto;

  loading = true;
  /** Error al cargar la ficha (no existe, sin permiso, red): panel con Reintentar / Volver. */
  error = '';
  /** El listado de asociados se carga aparte: un fallo suyo (p. ej. sin permiso) NO tumba la ficha. */
  asociadosCargando = false;
  asociadosError = '';
  /** Ejercicio (año) del que se muestran los asociados. */
  ejercicioAsociados: number | null = null;

  @Input()
  item!: any;

  @Input()
  parent?: any;

  @Output()
  openItem = new EventEmitter<ItemEvent | FormEvent>();

  @Output()
  back = new EventEmitter<any>();

  viewMode: ViewModeAsociado = ViewModeAsociado.INFO;
  preUrl: string = '';

  asociados: Asociado[] = [];
  asociadosToShow: Asociado[] = this.asociados;
  asociacionId: number = -1;

  searchControl = new FormControl();

  showEdit = false;
  saving = false;
  editForm = new FormGroup({});
  buttons = [
    FORM_BUTTONS[FormButtonType.Borrar],
    FORM_BUTTONS[FormButtonType.Cancelar],
  ]
  fields: Field[] = [];
  type!: ItemType;

  constructor(
    private route: ActivatedRoute,
    private asociacionesService: AsociacionesService,
    private router: Router,
    private formularioService: FormularioService,
    private alertService: AlertService,
    private ejercicioVigente: EjercicioVigenteService,
    private asociadosDeAsociacion: AsociacionAsociadosService,
    private nav: NavegacionService,
    private migas: MigasService,
    protected permisos: PermisosService
  ) { }

  private cargaAsociados: Subscription | null = null;

  ngOnInit() {
    if(!Boolean(this.item)) {
      this.asociacionId = Number(this.route.snapshot.paramMap.get('id'));
      this.preUrl = this.route.snapshot.url[0].path;
    }
    this.loadData();
  }

  loadData() {
    this.error = '';
    if (this.asociacionId === -1 && Boolean(this.item) && Boolean(this.item.id)) {
      this.asociacionId = this.item.id;
    }
    if (this.asociacionId > 0) {
      this.loading = true;
      this.asociacionesService.asociacionesGetById(this.asociacionId).subscribe({
        next: (res: ResponseAsociaciones) => {
          const encontrada = res.status?.status === 200 ? res.asociaciones?.[0] : undefined;
          if (!encontrada) {
            this.fallar('La asociación no existe.');
            return;
          }
          this.item = encontrada;
          this.migas.etiqueta('asociacion:' + this.asociacionId, encontrada.nombre);
          this.loading = false;
          this.cargarAsociados();
        },
        error: (err: HttpErrorResponse) => this.fallar(mensajeErrorCarga(err, 'la asociación'))
      });
    } else {
      this.fallar('La asociación no existe.');
    }
  }

  private fallar(mensaje: string) {
    this.error = mensaje;
    this.loading = false;
    this.alertService.danger(mensaje, 5000);
  }

  /**
   * Asociados de la asociación en el ejercicio que corresponde: el del ejercicio padre si la ficha se abre desde un ejercicio;
   * si no, el VIGENTE (activo en Censo), nunca el año natural. Con permisos insuficientes se muestra el motivo en la pestaña.
   */
  cargarAsociados() {
    this.cargaAsociados?.unsubscribe();
    this.asociadosError = '';
    this.asociadosCargando = true;
    const anyoPadre = Number(this.parent?.ejercicio);
    const ejercicio$ = anyoPadre > 0 ? of(anyoPadre) : this.ejercicioVigente.obtener().pipe(map(vigente => vigente?.ejercicio ?? null));
    this.cargaAsociados = ejercicio$.pipe(
      switchMap(anyo => { this.ejercicioAsociados = anyo; return this.asociadosDeAsociacion.listar(this.asociacionId, anyo); })
    ).subscribe({
      next: lista => {
        this.asociados = [...lista].sort((a, b) => String(a.apellidos ?? '').localeCompare(String(b.apellidos ?? ''), 'es') || String(a.nombre ?? '').localeCompare(String(b.nombre ?? ''), 'es'));
        this.asociadosToShow = this.asociados;
        this.asociadosCargando = false;
      },
      error: (err: HttpErrorResponse) => {
        this.asociados = [];
        this.asociadosToShow = [];
        this.asociadosError = err?.status === 403
          ? 'No tienes permiso para ver los asociados de esta asociación.'
          : mensajeErrorCarga(err, 'el listado de asociados');
        this.asociadosCargando = false;
      }
    });
  }

  volverAlListado() {
    this.nav.volverAlListado('/asociaciones');
  }

  // --- Acciones de la ficha (UX: la API vuelve a validar cada una) ----------------------------------------------------
  get puedeEditar(): boolean { return this.permisos.tieneEnAsociacion('asociaciones:escribir', this.item?.id); }
  /** Restablecimiento administrativo de la contraseña de OTRA cuenta (permiso global; la API lo vuelve a exigir). */
  get puedeRestablecer(): boolean { return this.permisos.tieneGlobal('contrasenas:restablecer'); }
  /** La propia asociación autenticada: su cambio de contraseña es otro flujo (exige la contraseña actual). */
  get esCuentaPropia(): boolean {
    const contexto = this.permisos.contexto();
    return contexto?.tipo === 'asociacion' && contexto.id === this.item?.id;
  }
  get puedeCambiarClaves(): boolean { return this.esCuentaPropia || this.puedeRestablecer; }
  get puedeGestionarEstado(): boolean { return this.permisos.tieneGlobal('asociaciones:estado'); }

  /** «Estado y acceso» vive en la pantalla de edición (con su propio botón, independiente de los datos). */
  abrirEstado() {
    this.editItem();
    setTimeout(() => (document.querySelector('[data-testid="estado-acceso"]') as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  // «Dar de baja» está RETIRADA del menú (decisión del 18/10): hoy la baja y la desactivación escriben el mismo campo (active = 0) y no
  // deben presentarse como equivalentes. El endpoint DELETE /asociaciones/{id} y su permiso (bajas:gestionar) se conservan; la acción
  // volverá cuando el modelo de datos distinga baja administrativa y desactivación de acceso (docs/p1.3-wave3-navegacion.md §6).
  // El diálogo reforzado app-confirmar-baja se conserva para entonces.

  editItem() {
    const names = ['nombre', 'cif', 'lema', 'anyo_fundacion', 'telefono', 'email', 'direccion', 'codigo_postal', 'localidad', 'provincia', 'latitud', 'longitud', 'sede_direccion', 'sede_codigo_postal', 'sede_poblacion', 'sede_provincia', 'sede_latitud', 'sede_longitud', 'ubicacion_monumento', 'ubicacion_monumento_codigo_postal', 'ubicacion_monumento_localidad', 'ubicacion_monumento_provincia', 'ubicacion_monumento_latitud', 'ubicacion_monumento_longitud', 'ubicacion_foguera_infantil', 'ubicacion_foguera_infantil_codigo_postal', 'ubicacion_foguera_infantil_localidad', 'ubicacion_foguera_infantil_provincia', 'ubicacion_foguera_infantil_latitud', 'ubicacion_foguera_infantil_longitud', 'ubicacion_raco', 'ubicacion_raco_codigo_postal', 'ubicacion_raco_localidad', 'ubicacion_raco_provincia', 'ubicacion_raco_latitud', 'ubicacion_raco_longitud', 'ubicacion_portada', 'ubicacion_portada_codigo_postal', 'ubicacion_portada_localidad', 'ubicacion_portada_provincia', 'ubicacion_portada_latitud', 'ubicacion_portada_longitud', 'ubicacion_barraca', 'ubicacion_barraca_codigo_postal', 'ubicacion_barraca_localidad', 'ubicacion_barraca_provincia', 'ubicacion_barraca_latitud', 'ubicacion_barraca_longitud'];
    this.editForm = new FormGroup(Object.fromEntries(names.map(name => [name, new FormControl(this.item?.[name] ?? '')])));
    this.showEdit = true;
  }

  location(prefix: string): StructuredLocation { const c = this.editForm.controls as any; return { direccion: c[prefix]?.value || '', codigo_postal: c[`${prefix}_codigo_postal`]?.value || '', localidad: c[`${prefix}_localidad`]?.value || '', provincia: c[`${prefix}_provincia`]?.value || '', latitud: c[`${prefix}_latitud`]?.value || null, longitud: c[`${prefix}_longitud`]?.value || null }; }
  setLocation(prefix: string, location: StructuredLocation): void { const patch: any = { [prefix]: location.direccion, [`${prefix}_codigo_postal`]: location.codigo_postal, [`${prefix}_localidad`]: location.localidad, [`${prefix}_provincia`]: location.provincia, [`${prefix}_latitud`]: location.latitud, [`${prefix}_longitud`]: location.longitud }; if (prefix === 'direccion') Object.assign(patch, { codigo_postal: location.codigo_postal, localidad: location.localidad, provincia: location.provincia, latitud: location.latitud, longitud: location.longitud }); if (prefix === 'sede_direccion') Object.assign(patch, { sede_codigo_postal: location.codigo_postal, sede_poblacion: location.localidad, sede_provincia: location.provincia, sede_latitud: location.latitud, sede_longitud: location.longitud }); this.editForm.patchValue(patch); }
  onEstadoCambiado(active: number | null): void { this.item = { ...this.item, active }; }
  isFoguera(): boolean { return Number(this.item?.tipo_asociacion) === 2; }
  saveAssociation(): void { if (this.saving) return; this.saving = true; const body = { ...this.editForm.getRawValue(), id: this.item.id }; this.asociacionesService.asociacionesPut(body as Asociacion, this.item.id).subscribe({ next: (res: any) => { this.item = res.asociaciones?.[0] || { ...this.item, ...body }; this.saving = false; this.showEdit = false; this.alertService.success('Datos de la asociación guardados.', 4000); }, error: (err: HttpErrorResponse) => { this.saving = false; this.alertService.danger(err.error?.status?.message || 'No se han podido guardar los datos.', 5000); } }); }

  goBack(event?: any) {
    if (this.preUrl === 'ejercicios') {
      this.back.emit(event);
    } else {
      this.nav.volver('/asociaciones');
    }
  }

  handleClick(section: ViewModeAsociado) {
    this.viewMode = section;
  }

  filterItems(value: string | any) {
    value = (value.target as HTMLInputElement).value.toLowerCase();
    this.asociadosToShow = this.asociados.filter(item => {
      const nombre = item.nombre ? item.nombre.toLowerCase() : '';
      const apellidos = item.apellidos ? item.apellidos.toLowerCase() : '';
      const nombreCompleto = `${nombre} ${apellidos}`;
      const nif = item.nif ? item.nif.toLowerCase() : '';
      return nif.includes(value) || nombreCompleto.includes(value);
    });
  }

}
